import { useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  KeyRound,
  Shield,
  X,
  ZoomIn,
} from "lucide-react";
import { Button } from "../shadcn/Button";
import Notice from "./Notice";
import qrCode from "../images/enrollment_qr.png";
import paso1 from "../images/paso1.png";
import paso1dot1 from "../images/paso1.1.png";
import paso1dot2 from "../images/paso1.2.png";
import homeScreen from "../images/kreceScreen.png";
import securityQr from "../images/security_enrollment_qr.png";
import { Checkbox } from "../shadcn/Checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../shadcn/Dialog";
import installSource from "../images/security_enrollment/authorize_source.webp";
import installNotifications from "../images/security_enrollment/notifications.webp";
import installScreen from "../images/security_enrollment/screen_timeout.webp";
import installDeveloper from "../images/security_enrollment/developer_mode.webp";
import installWireless from "../images/security_enrollment/wireless_debugging.webp";
import installNetwork from "../images/security_enrollment/network_permission.webp";
import installPairing from "../images/security_enrollment/pairing.webp";
import installOpenPairing from "../images/security_enrollment/open_pairing.webp";
import installSend from "../images/security_enrollment/send_code.webp";
import installConnected from "../images/security_enrollment/connected.webp";
import installLicense from "../images/security_enrollment/license.webp";
import installLock from "../images/security_enrollment/lock_screen.webp";

export function getEnrollmentError(
  message: unknown,
  fallback = "No pudimos confirmar la configuración del equipo. Reintenta o contacta a soporte de tiendas.",
  internalFallback = fallback,
) {
  if (typeof message !== "string" || !message.trim()) return fallback;
  return /\b(trustonic|tonic|nuovo(?:pay)?|globetek|kandji|absolute|paytrigger|knox(?:[_ -]guard)?|honor[_ -]guard|datacultr|mdm|proveedor)\b/i.test(
    message,
  )
    ? internalFallback
    : message;
}

export function EnrollmentIdentity({
  imei,
  deviceType,
}: {
  imei: string;
  deviceType: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-800 px-3 py-2 text-xs text-neutral-300">
      <span>
        {deviceType === "laptop"
          ? "Laptop"
          : deviceType === "iphone"
            ? "iPhone"
            : "Equipo"}
      </span>
      <span className="break-all">
        {deviceType === "laptop" ? "Serial" : "IMEI"}: {imei}
      </span>
    </div>
  );
}

export function EnrollmentReady({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id="enrollment-ready"
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
      />
      <label htmlFor="enrollment-ready" className="text-sm text-neutral-300">
        Completé la configuración en el equipo.
      </label>
    </div>
  );
}

type InstallationPhoto = { src: string; title: string; note?: string };

type DatacultrEnrollmentInstructionsProps = {
  imei: string;
  isLoading: boolean;
  error: string;
  onVerify: () => void;
  onBack: () => void;
};

const installationPhotos = {
  source: { src: installSource, title: "Autoriza la instalación desde Chrome" },
  notifications: {
    src: installNotifications,
    title: "Permite las notificaciones de Device Pro",
  },
  screen: {
    src: installScreen,
    title: "Selecciona 30 minutos de tiempo de pantalla",
  },
  developer: {
    src: installDeveloper,
    title: "Toca Número de compilación o Versión n.º siete veces",
  },
  wireless: { src: installWireless, title: "Activa Depuración inalámbrica" },
  network: {
    src: installNetwork,
    title: "Marca Permitir siempre en esta red y pulsa Permitir",
    note: "Datos de red ocultos en la foto de referencia.",
  },
  pairing: {
    src: installPairing,
    title: "Selecciona Emparejar dispositivo con código de sincronización",
    note: "Dirección de red oculta en la foto de referencia.",
  },
  open: {
    src: installOpenPairing,
    title: "Toca el texto Depuración inalámbrica, no el botón a su lado",
  },
  send: {
    src: installSend,
    title: "Envía los seis dígitos desde la notificación de Device Pro",
    note: "Código e IP ocultos. Usa el código del equipo que estás configurando.",
  },
  connected: {
    src: installConnected,
    title: "Espera Depuración inalámbrica conectada",
    note: "Dirección de red oculta en la foto de referencia.",
  },
  license: {
    src: installLicense,
    title: "Lee la licencia, baja hasta el final y pulsa Aceptar",
  },
  lock: {
    src: installLock,
    title: "Comprueba la marca de agua en la pantalla de bloqueo",
  },
};

export function DatacultrEnrollmentInstructions({
  imei,
  isLoading,
  error,
  onVerify,
  onBack,
}: DatacultrEnrollmentInstructionsProps) {
  const [step, setStep] = useState(1);
  const [photo, setPhoto] = useState<{
    items: InstallationPhoto[];
    index: number;
  } | null>(null);
  const [zoom, setZoom] = useState(false);
  const titles = [
    "Escanea el código QR",
    "Instala la app de seguridad",
    "Prepara los ajustes",
    "Abre el emparejamiento",
    "Vincula el teléfono",
    "Finaliza y verifica",
  ];
  const lastStep = titles.length;
  const move = (next: number) => {
    if (isLoading) return;
    setStep(next);
  };
  const openPhotos = (items: InstallationPhoto[]) => {
    setZoom(false);
    setPhoto({ items, index: 0 });
  };
  const instruction = (
    title: string,
    content: ReactNode,
    photos: InstallationPhoto[],
  ) => (
    <section className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-100">{title}</h3>
      </div>
      <div className="text-sm leading-5 text-neutral-300">{content}</div>
      <button
        type="button"
        aria-label={`Ampliar: ${title}`}
        onClick={() => openPhotos(photos)}
        className="mt-auto block w-full rounded-lg text-center focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <img
          src={photos[0].src}
          alt={photos[0].title}
          className="mx-auto h-[120px] max-w-full object-contain md:h-[160px]"
        />
        <span className="mt-1 inline-flex items-center gap-1 text-xs text-neutral-400">
          <ZoomIn className="size-3" />
          Ver {photos.length > 1 ? "fotos" : "foto"}
        </span>
      </button>
    </section>
  );
  return (
    <div data-installation-step={step} className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-neutral-0 md:text-2xl">
            {titles[step - 1]}
          </h2>
          <p className="mt-1 text-xs text-neutral-400">
            Paso {step} de {titles.length} · IMEI: {imei}
          </p>
        </div>
        <Shield className="mt-1 size-5 shrink-0 text-primary-500" />
      </div>
      {step === 1 && (
        <>
          <p className="text-sm leading-5 text-neutral-300">
            Completa el inicio sin Wi-Fi ni cuenta Google. Después conecta el
            Wi-Fi de la tienda.
          </p>
          <div className="flex flex-col items-center gap-3 py-2">
            <img
              src={securityQr}
              alt="Código de instalación de seguridad"
              className="h-[200px] w-[200px] bg-white p-1"
            />
            <p className="max-w-sm text-center text-sm leading-5 text-neutral-300">
              Abre Chrome sin cuenta. Usa el ícono de cámara de la barra de
              búsqueda para escanear este QR y abre el enlace.
            </p>
          </div>
          <p className="text-xs text-neutral-400">
            Si Chrome muestra su aviso de privacidad, pulsa Entendido.
          </p>
        </>
      )}
      {step === 2 && (
        <div className="grid grid-cols-2 gap-3 md:gap-5">
          {instruction(
            "Descarga e instala",
            <p>
              Espera la descarga y abre{" "}
              <span className="break-all">KRECE.apk</span>. Si Chrome
              lo pide, entra a Configuración, activa{" "}
              <b>Autorizar de esta fuente</b> y pulsa Instalar.
            </p>,
            [installationPhotos.source],
          )}
          {instruction(
            "Abre y permite",
            <p>
              Abre la app instalada, Device Pro, <b>permite las notificaciones</b>{" "}
              y toca Comenzar. No pulses Omitir en la app.
            </p>,
            [installationPhotos.notifications],
          )}
        </div>
      )}
      {step === 3 && (
        <>
          <p className="text-sm leading-5 text-neutral-300">
            En cada paso de la app toca Habilitar. Después de cada ajuste,
            vuelve a la app y toca Siguiente.
          </p>
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            {instruction(
              "Tiempo de pantalla",
              <p>
                En Pantalla y brillo, cambia la desactivación automática a{" "}
                <b>30 minutos</b>.
              </p>,
              [installationPhotos.screen],
            )}
            {instruction(
              "Modo desarrollador",
              <p>
                En Información del dispositivo, abre Versión. Toca{" "}
                <b>7 veces</b> Número de compilación o Versión n.º hasta ver "Ya
                eres desarrollador".
              </p>,
              [installationPhotos.developer],
            )}
          </div>
          <p className="text-xs text-neutral-400">
            Los nombres de los ajustes pueden variar según la marca.
          </p>
        </>
      )}
      {step === 4 && (
        <>
          <p className="text-sm leading-5 text-neutral-300">
            Mantén el teléfono conectado al Wi-Fi de la tienda.
          </p>
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            {instruction(
              "Depuración inalámbrica",
              <p>
                En el Paso 3 de la app, toca Habilitar y activa{" "}
                <b>Depuración inalámbrica</b>. Marca Permitir siempre en esta
                red, toca Permitir y vuelve a la app para pulsar Siguiente.
              </p>,
              [installationPhotos.wireless, installationPhotos.network],
            )}
            {instruction(
              "Abre el código",
              <p>
                En el Paso 4 de la app, toca Abrir. Toca el <b>texto</b>{" "}
                Depuración inalámbrica, no el botón a su lado. Elige Emparejar
                dispositivo con código de sincronización.
              </p>,
              [installationPhotos.pairing, installationPhotos.open],
            )}
          </div>
        </>
      )}
      {step === 5 && (
        <>
          <Notice
            variant="warning"
            message="No cierres la ventana del código mientras abres la notificación y lo envías."
          />
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            {instruction(
              "Envía los 6 dígitos",
              <p>
                Baja las notificaciones. En Device Pro, toca{" "}
                <b>Código de emparejamiento</b>, escribe los 6 dígitos y envía.
                No los ingreses en este portal.
              </p>,
              [installationPhotos.send],
            )}
            {instruction(
              "Espera la conexión",
              <p>
                Permanece allí hasta ver <b>Depuración inalámbrica conectada</b>
                . Puede tardar hasta 60 segundos.
              </p>,
              [installationPhotos.connected],
            )}
          </div>
        </>
      )}
      {step === lastStep && (
        <>
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            {instruction(
              "Completa la licencia",
              <p>
                Si Google pide una cuenta, toca Omitir. Lee la licencia de la
                app, baja hasta el final y pulsa <b>Aceptar</b>.
              </p>,
              [installationPhotos.license],
            )}
            {instruction(
              "Revisa el bloqueo",
              <p>
                Cuando vuelva al inicio, bloquea el teléfono. Comprueba la marca
                de agua <b>"Este dispositivo pertenece…"</b>.
              </p>,
              [installationPhotos.lock],
            )}
          </div>
          <p className="text-xs text-neutral-400">
            Al pulsar Siguiente, verificaremos la instalación en el equipo.
            Si aún no está lista, podrás corregirla y volver a intentar.
          </p>
          {error && (
            <Notice variant="error" message={getEnrollmentError(error)} />
          )}
        </>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-900 pt-3">
        <Button
          variant="secondary"
          className="!w-auto"
          disabled={isLoading}
          Icon={<ArrowLeft className="hidden size-4 sm:block" />}
          onClick={() => (step === 1 ? onBack() : move(step - 1))}
        >
          Atrás
        </Button>
        {step < lastStep ? (
          <>
            <Button
              variant="text"
              className="!w-auto !px-2 text-xs sm:text-sm"
              disabled={isLoading}
              onClick={() => move(lastStep)}
            >
              Saltar instrucciones
            </Button>
            <Button
              variant="primary"
              className="!w-auto"
              disabled={isLoading}
              onClick={() => move(step + 1)}
            >
              Siguiente
              <ArrowRight className="hidden size-4 sm:block" />
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            className="!w-auto"
            aria-label="Siguiente: verificar instalación"
            loading={isLoading}
            disabled={isLoading}
            onClick={() => {
              if (!isLoading) onVerify();
            }}
          >
            Siguiente
          </Button>
        )}
      </div>
      <Dialog
        open={photo !== null}
        onOpenChange={(open) => {
          if (!open) setPhoto(null);
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="max-h-[94dvh] !max-w-[min(94vw,720px)] overflow-auto border-neutral-800 p-4 md:p-6"
        >
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="leading-snug text-neutral-100">
              {photo?.items[photo.index].title}
            </DialogTitle>
            <DialogClose asChild>
              <Button
                variant="text"
                className="!h-8 !w-8 shrink-0 !p-1"
                aria-label="Cerrar foto"
              >
                <X className="size-5" />
              </Button>
            </DialogClose>
          </div>
          <DialogDescription className="text-sm text-neutral-400">
            Foto de referencia. Los menús pueden variar según el equipo.
          </DialogDescription>
          {photo && (
            <>
              <img
                src={photo.items[photo.index].src}
                alt={photo.items[photo.index].title}
                className={
                  zoom
                    ? "mx-auto h-auto w-full max-w-[620px] object-contain"
                    : "mx-auto max-h-[60dvh] max-w-full object-contain"
                }
              />
              {photo.items[photo.index].note && (
                <p className="text-xs text-neutral-400">
                  {photo.items[photo.index].note}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button
                  variant="text"
                  className="!w-auto"
                  onClick={() => setZoom(!zoom)}
                >
                  {zoom ? "Ajustar foto" : "Ampliar más"}
                </Button>
                {photo.items.length > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      className="!w-auto"
                      aria-label="Foto anterior"
                      disabled={photo.index === 0}
                      onClick={() => {
                        setZoom(false);
                        setPhoto({ ...photo, index: photo.index - 1 });
                      }}
                    >
                      <ArrowLeft className="size-4" />
                    </Button>
                    <span className="text-xs text-neutral-400">
                      {photo.index + 1} / {photo.items.length}
                    </span>
                    <Button
                      variant="secondary"
                      className="!w-auto"
                      aria-label="Foto siguiente"
                      disabled={photo.index === photo.items.length - 1}
                      onClick={() => {
                        setZoom(false);
                        setPhoto({ ...photo, index: photo.index + 1 });
                      }}
                    >
                      <ArrowRight className="size-4" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function LaptopEnrollmentInstructions() {
  return (
    <ol className="space-y-3 text-neutral-300">
      <li>1. Encienda la laptop.</li>
      <li>
        2. Abra el buscador y descargue el archivo zip de{" "}
        <a
          className="text-primary-400 underline light:text-primary-700"
          href="https://www.krece.app/laptop-zip"
          target="_blank"
          rel="noopener noreferrer"
        >
          krece.app/laptop-zip
        </a>
        .
      </li>
      <li>
        3. Extraiga el archivo zip y ejecute el archivo .exe que se encuentra
        dentro. Siga los pasos de instalación.
      </li>
      <li>4. Una vez instalado, reinicie la laptop.</li>
      <li>5. Espere 15 minutos antes de ingresar el serial.</li>
    </ol>
  );
}

export function SecurityPermissionInstructions({
  step,
}: {
  step: "background" | "complete";
}) {
  return (
    <ol className="space-y-3 text-neutral-300">
      {step === "background" ? (
        <>
          <li>1. En la app de seguridad, pulse "Ajustes adicionales".</li>
          <li>
            2. Cuando el teléfono pregunte si permite que la app se ejecute
            siempre en segundo plano, pulse "PERMITIR".
          </li>
        </>
      ) : (
        <>
          <li>
            1. Active las notificaciones en segundo plano desde el botón de
            ajustes de la app de seguridad.
          </li>
          <li>
            2. Complete la configuración de instalación de la aplicación y
            habilite los permisos que solicita el teléfono.
          </li>
          <li>
            3. Cuando las indicaciones estén completas, pulse "CONFIGURACIÓN
            COMPLETA".
          </li>
          <li>
            4. Tenga a mano el código de confirmación que aparece al finalizar.
          </li>
        </>
      )}
    </ol>
  );
}

export function NuovoEnrollmentInstructions({ country }: { country: string }) {
  return (
    <div className="space-y-4">
      <p className="text-neutral-300">
        Encienda el dispositivo nuevo y en la primera pantalla cambie el idioma
        a Español. En esa misma pantalla, toque en cualquier lugar de la
        pantalla 6 veces para ver un escáner de código QR.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <img
          className="w-full"
          src={country === "venezuela" ? paso1 : paso1dot1}
          alt="Paso 1"
        />
        <img className="w-full" src={paso1dot2} alt="Paso 1.2" />
      </div>
      <p className="text-neutral-300">
        2. Escanee el siguiente código QR para iniciar la instalación del app de
        seguridad.
      </p>
      <div className="flex justify-center">
        <img className="w-[200px]" src={qrCode} alt="Enrollment QR" />
      </div>
    </div>
  );
}

type IphoneEnrollmentInstructionsProps = {
  imei?: string;
  bypassCode: string;
  bypassCodeLoading: boolean;
  bypassCodeError: string;
  onGetBypassCode: () => void;
  onCopyBypassCode: () => void;
};

export function IphoneEnrollmentInstructions({
  imei,
  bypassCode,
  bypassCodeLoading,
  bypassCodeError,
  onGetBypassCode,
  onCopyBypassCode,
}: IphoneEnrollmentInstructionsProps) {
  return (
    <>
      <div className="text-center text-neutral-300">
        Por favor proceda a encender el equipo, unirlo con el WIFI de la tienda,
        aceptar todas las condiciones y realizar la configuración hasta el
        final.
      </div>
      {imei && (
        <div className="space-y-3">
          <p className="text-center text-xs text-neutral-500">
            ¿El equipo solicita usuario y contraseña? Genere el código de
            bypass, deje el campo de usuario vacío y use el código como
            contraseña.
          </p>
          <Button
            variant="secondary"
            className="w-full text-xs"
            Icon={<KeyRound className="size-3" />}
            onClick={onGetBypassCode}
            loading={bypassCodeLoading}
            disabled={bypassCodeLoading || !!bypassCode}
            type="button"
          >
            {bypassCode ? "Código generado" : "Obtener código de bypass"}
          </Button>
          {bypassCode && (
            <div className="flex items-center justify-between rounded-md bg-neutral-800/50 px-3 py-2">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-primary-500" />
                <code className="font-mono text-sm text-primary-400">
                  {bypassCode}
                </code>
              </div>
              <button
                type="button"
                onClick={onCopyBypassCode}
                className="p-1 text-neutral-500 hover:text-neutral-300"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          )}
          {bypassCodeError && (
            <Notice message={bypassCodeError} variant="error" />
          )}
        </div>
      )}
    </>
  );
}

export function ManagedAndroidEnrollmentInstructions() {
  return (
    <>
      <div className="text-center text-neutral-300">
        Por favor proceda a encender el equipo, unirlo con el WIFI de la tienda,
        aceptar todas las condiciones y realizar la configuración hasta que vea
        la siguiente pantalla.
      </div>
      <Notice
        message="IMPORTANTE (no aplica para Samsung): Si no logra llegar a la pantalla mostrada abajo, termine la configuración e intente restaurar de fábrica. Si el equipo permite restaurar, significa que no se configuró correctamente y debe repetir el proceso. De lo contrario, si no permite restaurar, puede continuar al siguiente paso."
        variant="warning"
      />
      <div className="flex justify-center">
        <img
          className="h-[448px] w-[200px]"
          src={homeScreen}
          alt="Pantalla principal de Krece"
        />
      </div>
    </>
  );
}
